import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdentifierDefinitionDto } from '@/api/client';
import { getErrorCodeForUi, getFieldViolations } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import type {
  FhirIdentifierMappingRequest,
  FhirIdentifierMappingViewModel,
} from '../types';

interface DraftState {
  fhirSystemValue: string;
  identifierGuid: string;
  label: string;
  usedForPatientIdResolution: boolean;
  active: boolean;
}

const EMPTY_DRAFT: DraftState = {
  fhirSystemValue: '',
  identifierGuid: '',
  label: '',
  usedForPatientIdResolution: false,
  active: true,
};

function draftFromRow(row: FhirIdentifierMappingViewModel): DraftState {
  return {
    fhirSystemValue: row.fhirSystemValue,
    identifierGuid: row.identifierGuid,
    // Only seed the override box with an explicit override, not the resolved fallback.
    label: row.label === row.identifierLabel ? '' : row.label,
    usedForPatientIdResolution: row.usedForPatientIdResolution,
    active: row.active,
  };
}

interface FieldErrors {
  fhirSystemValue?: string;
  identifierGuid?: string;
}

export function IdentifierMappingDialog({
  open,
  mode,
  initial,
  identifierOptions,
  identifierOptionsLoading,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  readonly mode: 'create' | 'edit';
  readonly initial: FhirIdentifierMappingViewModel | null;
  readonly identifierOptions: readonly IdentifierDefinitionDto[];
  readonly identifierOptionsLoading: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (body: FhirIdentifierMappingRequest) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});

  // Re-seed only when a different row opens (keyed on guid), so a background
  // refetch of the list does not clobber an in-progress edit.
  const seededGuid = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededGuid.current = null;
      return;
    }
    const key = initial?.guid ?? '__create__';
    if (seededGuid.current === key) return;
    seededGuid.current = key;
    setDraft(initial !== null ? draftFromRow(initial) : EMPTY_DRAFT);
    setErrors({});
  }, [open, initial]);

  function handleClose(next: boolean): void {
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: FieldErrors = {};
    const fhirSystemValue = draft.fhirSystemValue.trim();
    const identifierGuid = draft.identifierGuid.trim();
    if (fhirSystemValue.length === 0) {
      next.fhirSystemValue = t(
        'routes.admin.fhirIntegrations.mappings.dialog.errors.fhirSystemValueRequired',
      );
    }
    if (identifierGuid.length === 0) {
      next.identifierGuid = t(
        'routes.admin.fhirIntegrations.mappings.dialog.errors.identifierRequired',
      );
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    const label = draft.label.trim();
    onSubmit({
      fhirSystemValue,
      identifierGuid,
      ...(label.length > 0 ? { label } : {}),
      usedForPatientIdResolution: draft.usedForPatientIdResolution,
      active: draft.active,
    });
  }

  // Server-side error routing: 422 VALIDATION_FAILED → field-scoped; 409/404 →
  // mapped to the field they concern via the ApiError code.
  const violations = getFieldViolations(submitError);
  const code = getErrorCodeForUi(submitError);
  const serverFhirSystemValueError =
    violations.find((v) => v.field === 'fhirSystemValue')?.message ??
    (code === 'conflict'
      ? t('routes.admin.fhirIntegrations.mappings.dialog.errors.duplicate')
      : undefined);
  const serverIdentifierError =
    violations.find((v) => v.field === 'identifierGuid')?.message ??
    (code === 'not_found'
      ? t('routes.admin.fhirIntegrations.mappings.dialog.errors.identifierNotFound')
      : undefined);

  const fhirSystemValueError = errors.fhirSystemValue ?? serverFhirSystemValueError;
  const identifierError = errors.identifierGuid ?? serverIdentifierError;

  // A submit error that landed on neither field (e.g. server_error) gets a banner.
  const hasUnroutedError =
    submitError !== undefined &&
    submitError !== null &&
    fhirSystemValueError === undefined &&
    identifierError === undefined;

  const title =
    mode === 'create'
      ? t('routes.admin.fhirIntegrations.mappings.dialog.createTitle')
      : t('routes.admin.fhirIntegrations.mappings.dialog.editTitle');
  const submitLabel =
    mode === 'create'
      ? t('routes.admin.fhirIntegrations.mappings.dialog.create')
      : t('routes.admin.fhirIntegrations.mappings.dialog.save');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-lg" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormField
            id="fhir-mapping-system-value"
            label={t('routes.admin.fhirIntegrations.mappings.dialog.fields.fhirSystemValue')}
            required
            error={fhirSystemValueError}
          >
            {(fp) => (
              <Input
                {...fp}
                value={draft.fhirSystemValue}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, fhirSystemValue: e.target.value }));
                }}
                autoComplete="off"
                spellCheck={false}
                placeholder="urn:oid:2.16…"
                className="font-mono text-sm"
              />
            )}
          </FormField>

          <FormField
            id="fhir-mapping-identifier"
            label={t('routes.admin.fhirIntegrations.mappings.dialog.fields.identifier')}
            required
            error={identifierError}
          >
            {(fp) => (
              <Select
                {...fp}
                value={draft.identifierGuid}
                disabled={identifierOptionsLoading}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, identifierGuid: e.target.value }));
                }}
              >
                <option value="">
                  {t('routes.admin.fhirIntegrations.mappings.dialog.fields.identifierPlaceholder')}
                </option>
                {identifierOptions.map((opt) => (
                  <option key={opt.id ?? ''} value={opt.id ?? ''}>
                    {t('routes.admin.fhirIntegrations.mappings.table.identifierValue', {
                      label: opt.label ?? '',
                      userEnteredId: opt.userEnteredId ?? '',
                    })}
                  </option>
                ))}
              </Select>
            )}
          </FormField>

          <FormField
            id="fhir-mapping-label"
            label={t('routes.admin.fhirIntegrations.mappings.dialog.fields.label')}
          >
            {(fp) => (
              <Input
                {...fp}
                value={draft.label}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, label: e.target.value }));
                }}
                autoComplete="off"
                spellCheck={false}
                placeholder={t(
                  'routes.admin.fhirIntegrations.mappings.dialog.fields.labelPlaceholder',
                )}
              />
            )}
          </FormField>

          <div className="space-y-2 border-t border-border pt-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.usedForPatientIdResolution}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, usedForPatientIdResolution: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.fhirIntegrations.mappings.dialog.fields.usedForPatientIdResolution')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, active: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.fhirIntegrations.mappings.dialog.fields.active')}
            </label>
          </div>

          {hasUnroutedError && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.fhirIntegrations.mappings.dialog.errors.submit', { code })}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                handleClose(false);
              }}
              disabled={pending}
            >
              {t('routes.admin.fhirIntegrations.mappings.dialog.cancel')}
            </Button>
            <Button type="submit" loading={pending}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
